import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FileText, Clock, PhoneCall, BarChart3, HelpCircle, Bell, User,
  ArrowRight, Eye, TrendingUp,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Plain-English hint box (mirrors Lone Star's Hint component) ── */
const Hint = ({ text, children }) => (
  <div className="amg-hint">
    <HelpCircle size={15} />
    <span>{children || text}</span>
  </div>
);

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
};

const getDayString = () => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[new Date().getDay()];
};

const getTimeString = () =>
  new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const Overview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(getTimeString());

  useEffect(() => {
    const timer = setInterval(() => setTime(getTimeString()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('amg_token');
    const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    Promise.all([
      axios.get(`${API}/analytics/overview`, cfg),
      axios.get(`${API}/leads/recent`, cfg),
    ])
      .then(([a, b]) => {
        setStats(a.data);
        setRecent(b.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)', fontSize: '1.2rem' }}>Loading dashboard\u2026</div>;
  }

  const s = stats || {};
  const newCount = s.new_urgent || 0;
  const totalLeads = s.total_leads || 0;
  const qualified = s.qualified || 0;
  const unassigned = s.unassigned || 0;
  const thisWeek = s.this_week || 0;
  const lastWeek = s.last_week || 0;
  const greeting = getGreeting();
  const dayName = getDayString();
  const surname = (user?.name || 'Steve Cruz').split(' ').slice(-1)[0] || 'Cruz';

  const statusBadge = (status) => {
    const s = (status || 'New').toString();
    if (/urgent|new/i.test(s) && !/attempt|contact|qualified|bond|closed/i.test(s)) return <span className="badge-new">NEW</span>;
    if (/bond posted|closed won|printed/i.test(s)) return <span className="badge-printed">POSTED</span>;
    return <span className="badge-viewed">{s.toUpperCase()}</span>;
  };

  const newLeads = recent.filter((l) => /new|urgent/i.test(l.status || '') && !/attempt|contact|qualified/i.test(l.status || ''));

  return (
    <div data-testid="dash-overview">
      {/* ── Personalized Greeting ── */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          marginBottom: '2rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, rgba(13, 26, 19, 0.85), rgba(177, 133, 74, 0.08))',
          border: '1px solid rgba(177, 133, 74, 0.18)',
        }}
        data-testid="greeting-banner"
      >
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.7rem, 3vw, 2.4rem)', color: 'var(--text-primary)', letterSpacing: '0.02em', marginBottom: '0.35rem', lineHeight: 1.15, textTransform: 'uppercase' }}>
          Good {greeting}, Mr. {surname}.
        </h1>
        <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          Today is <strong style={{ color: 'var(--text-primary)' }}>{dayName}</strong> and it&apos;s currently{' '}
          <strong style={{ color: 'var(--gold)' }}>{time}</strong>
        </p>
      </motion.div>

      {/* ── New Submissions Alert ── */}
      {newCount > 0 ? (
        <Link
          to="/dashboard/leads"
          data-testid="new-alert-banner"
          style={{
            display: 'block', padding: '1.5rem', textDecoration: 'none',
            background: 'rgba(122, 30, 40, 0.10)',
            border: '2px solid rgba(122, 30, 40, 0.4)',
            marginBottom: '1.5rem',
            animation: 'pulse-subtle 2s infinite',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: newLeads.length > 0 ? '0.75rem' : 0 }}>
            <Bell size={28} style={{ color: 'var(--red-bright)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--text-primary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {newCount === 1
                  ? 'There is a new bond application'
                  : `There are ${newCount} new bond applications`}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3.5rem', color: 'var(--red-bright)', lineHeight: 1 }}>
              {newCount}
            </div>
          </div>
          {newLeads.length > 0 && (
            <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(122, 30, 40, 0.2)' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontFamily: 'var(--font-alt)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {newCount === 1 ? 'From:' : 'From these people:'}
              </div>
              {newLeads.slice(0, 5).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                  <User size={14} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                  <span style={{ fontSize: '1rem', color: 'var(--text-primary)', fontWeight: 700 }}>
                    {p.defendant_name || p.requester_name || 'Unknown'}
                  </span>
                  {p.jail_city && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      ({p.jail_city})
                    </span>
                  )}
                </div>
              ))}
              {newLeads.length > 5 && (
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
                  \u2026and {newLeads.length - 5} more
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--gold)', fontWeight: 700, fontFamily: 'var(--font-alt)', letterSpacing: '0.08em' }}>
            Click here to review them now <ArrowRight size={16} />
          </div>
        </Link>
      ) : (
        <div style={{ padding: '1.5rem', background: 'rgba(123, 160, 94, 0.06)', border: '1px solid rgba(123, 160, 94, 0.15)', marginBottom: '1.5rem' }} data-testid="no-new-banner">
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--success)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            All Caught Up!
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', margin: '0.35rem 0 0' }}>
            You have no new applications waiting. When someone fills out a form on your website, it will appear here.
          </p>
        </div>
      )}

      {/* ── Stats Cards ── */}
      <Hint>
        These boxes show you the big picture: how many bond applications have come in, how many are brand new, how many are ready for the next step, and how you compared to last week.
      </Hint>
      <div className="dash-stats" data-testid="stats-grid">
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><FileText size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> Total Applications</div>
          <div className="dash-stat-card__value">{totalLeads}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>All forms ever submitted</div>
        </div>
        <div className="dash-stat-card" style={{ borderColor: newCount > 0 ? 'rgba(122, 30, 40, 0.5)' : undefined }}>
          <div className="dash-stat-card__label"><Clock size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> New / Waiting</div>
          <div className={`dash-stat-card__value ${newCount > 0 ? 'dash-stat-card__value--alert' : ''}`}>{newCount}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Not yet touched by you</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><PhoneCall size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> Qualified+</div>
          <div className="dash-stat-card__value dash-stat-card__value--gold">{qualified}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Ready for bond posting</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-card__label"><BarChart3 size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} /> This Week</div>
          <div className="dash-stat-card__value">{thisWeek}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
            {thisWeek >= lastWeek ? <><TrendingUp size={11} style={{ display: 'inline', verticalAlign: 'middle', color: 'var(--success)' }} /> Up from</> : <>Down from</>} {lastWeek} last week
          </div>
        </div>
      </div>

      {/* ── How to use this dashboard (Quick Guide) ── */}
      <div style={{ padding: '1.25rem', background: 'rgba(177, 133, 74, 0.03)', border: '1px solid rgba(177, 133, 74, 0.12)', marginTop: '1.5rem', marginBottom: '1.5rem' }} data-testid="quick-guide">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--gold)', letterSpacing: '0.06em', marginBottom: '0.9rem', textTransform: 'uppercase' }}>
          How to use this dashboard
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.85rem' }}>
          {[
            { icon: <FileText size={18} />, title: 'SUBMISSIONS', desc: 'Click "SUBMISSIONS" in the sidebar to see every bond application. Click any row to open it and print the PDFs.' },
            { icon: <BarChart3 size={18} />, title: 'ANALYTICS', desc: 'Click "ANALYTICS" to see your business report \u2014 which cities send the most leads, weekly trends, and status breakdowns.' },
            { icon: <Eye size={18} />, title: 'NEW vs POSTED', desc: 'New applications glow red until you touch them. Once you contact the family they turn "VIEWED". Bond posted = "POSTED".' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <div style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '0.2rem', fontFamily: 'var(--font-alt)', letterSpacing: '0.08em' }}>{item.title}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.55 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Recent Submissions ── */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.6rem' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--text-primary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Recent Submissions
          </h2>
          <Link to="/dashboard/leads" className="amg-btn-outline" data-testid="view-all-submissions">
            View All <FileText size={14} />
          </Link>
        </div>
        <Hint>
          This table shows the most recent bond applications people submitted. <strong>NEW</strong> means you haven&apos;t looked at it yet. Click <strong>VIEW ALL</strong> to search everything and print PDFs.
        </Hint>

        {recent.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border-dim)', color: 'var(--text-muted)', fontSize: '1.05rem' }}>
            No submissions yet. When someone fills out a form on your website, it will show up here.
          </div>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table" data-testid="recent-submissions-table">
              <thead>
                <tr>
                  <th>STATUS</th>
                  <th>NAME</th>
                  <th>JAIL / CITY</th>
                  <th>SUBMITTED</th>
                </tr>
              </thead>
              <tbody>
                {recent.slice(0, 8).map((sub, i) => (
                  <tr
                    key={sub.id || i}
                    className="clickable"
                    onClick={() => window.location.assign(`/dashboard/leads/${sub.id}`)}
                    data-testid={`overview-lead-row-${sub.id}`}
                  >
                    <td>{statusBadge(sub.status)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '1rem' }}>
                      {sub.defendant_name || sub.requester_name || 'N/A'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {sub.jail_city || '\u2014'}
                    </td>
                    <td style={{ fontFamily: 'var(--font-alt)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      {sub.created_at ? new Date(sub.created_at).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
